import React, { useState, useEffect } from 'react';
import { getUsers } from '../../services/userService';
import useAuthStore from '../../stores/authStore';
import api from '../../services/api';

const SystemDashboardPage: React.FC = () => {
  const { token } = useAuthStore();
  const [pendingUsersCount, setPendingUsersCount] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [newsletterSubscribersCount, setNewsletterSubscribersCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (token) {
          const users = await getUsers();
          setTotalUsers(users.length);
          const pending = users.filter((user: any) => user.status === 'pending_approval');
          setPendingUsersCount(pending.length);
          
          // Fetch newsletter subscribers count
          const subscribersResponse = await api.get('/newsletter/');
          setNewsletterSubscribersCount(subscribersResponse.data.length);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">System Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Pending Approvals Notification */}
        <div className="bg-yellow-50 p-6 rounded-lg shadow-md border border-yellow-200">
          <h2 className="text-xl font-semibold mb-4 text-yellow-800">Pending Approvals</h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-yellow-700">Users Waiting:</span>
              <span className="bg-yellow-500 text-white px-2 py-1 rounded-full text-sm font-bold">
                {loading ? '...' : pendingUsersCount}
              </span>
            </div>
            <div className="text-sm text-yellow-600">
              New user registrations awaiting approval
            </div>
            <button 
              onClick={() => window.location.href = '/admin/users'}
              className="w-full bg-yellow-600 text-white py-2 px-4 rounded hover:bg-yellow-700 transition-colors mt-4"
            >
              Review Users
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border">
          <h2 className="text-xl font-semibold mb-4">Server Status</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>API Server:</span>
              <span className="text-green-600 font-semibold">Online</span>
            </div>
            <div className="flex justify-between">
              <span>Database:</span>
              <span className="text-green-600 font-semibold">Connected</span>
            </div>
            <div className="flex justify-between">
              <span>Email Service:</span>
              <span className="text-green-600 font-semibold">Active</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border">
          <h2 className="text-xl font-semibold mb-4">System Statistics</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>Total Users:</span>
              <span className="font-semibold">{loading ? '...' : totalUsers}</span>
            </div>
            <div className="flex justify-between">
              <span>Newsletter Subscribers:</span>
              <span className="font-semibold">{loading ? '...' : newsletterSubscribersCount}</span>
            </div>
            <div className="flex justify-between">
              <span>Active Sessions:</span>
              <span className="font-semibold">--</span>
            </div>
            <div className="flex justify-between">
              <span>Storage Used:</span>
              <span className="font-semibold">--</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors">
              Clear Cache
            </button>
            <button className="w-full bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700 transition-colors">
              Backup Database
            </button>
            <button className="w-full bg-yellow-600 text-white py-2 px-4 rounded hover:bg-yellow-700 transition-colors">
              System Logs
            </button>
          </div>
        </div>
      </div>



      <div className="bg-white p-6 rounded-lg shadow-md border">
        <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
        <div className="text-gray-500 text-center py-8">
          Activity monitoring will be implemented soon
        </div>
      </div>
    </div>
  );
};

export default SystemDashboardPage;
